/**
 * What a PostHog session replay records beyond the page itself. Loaded by
 * posthog-init.js, so nothing here runs before analytics consent.
 *
 * 1. Typed text. Replays show what players type into the game's own fields
 *    (their name, the Moon Dream prompt, deck names, searches, quantities) and
 *    every dropdown and slider, so a recording reads like the session (owner,
 *    25 Oct 2026). Every other field stays masked: passwords, emails, voucher
 *    codes, the feedback box, and any field added later until it is listed.
 *
 * 2. Device context. The recorder keeps the page but not the device around
 *    it. Replay 01a16cf7 (24 Oct 2026) drew an iPhone's 62 px notch bands as
 *    0, so its Foundry menu appeared on the Stardust pill, and showed the
 *    keyboard as nothing. One small `device_context` event carries what the
 *    replay lacks, at the start and again only when a value changes.
 */

// Fields whose typing a replay shows. replayInputMasking.test.js checks that
// each one still exists in the game.
const REPLAY_VISIBLE_FIELDS = [
  'input.username-onboarding-input',
  '#moon-dream-prompt',
  '#deck-name-input',
  '#search-input',
  '#deck-builder-search',
  '#collection-search',
  '#moon-exchange-search',
  '#moon-exchange-card-search',
  'input.moon-exchange-buy-qty',
];
const VISIBLE_FIELD_SELECTOR = REPLAY_VISIBLE_FIELDS.join(',');
const ALWAYS_MASKED_TYPES = new Set(['password', 'email', 'tel']);
const ALWAYS_MASKED_AUTOCOMPLETE = /(^|\s)(cc-|one-time-code|email|tel|current-password|new-password)/;
const ALWAYS_SHOWN_TYPES = new Set(['range', 'checkbox', 'radio']);

function maskText(value) {
  return '*'.repeat(value.length);
}

// PostHog's maskInputFn: called for every field, since maskAllInputs stays on.
function replayInputText(text, element) {
  const value = text == null ? '' : String(text);
  if (!element || typeof element.matches !== 'function') return maskText(value);
  const type = String(element.getAttribute('type') || '').toLowerCase();
  if (ALWAYS_MASKED_TYPES.has(type)) return maskText(value);
  if (ALWAYS_MASKED_AUTOCOMPLETE.test(String(element.getAttribute('autocomplete') || '').toLowerCase())) {
    return maskText(value);
  }
  if (element.tagName === 'SELECT' || ALWAYS_SHOWN_TYPES.has(type)) return value;
  return element.matches(VISIBLE_FIELD_SELECTOR) ? value : maskText(value);
}

const SETTLE_MS = 400; // one event per pause, however many resize events fire
const FIRST_SEND_MS = 600; // first measurement after the page settles
const MAX_EVENTS_PER_PAGE = 200;

function round1(n) {
  return typeof n === 'number' && Number.isFinite(n) ? Math.round(n * 10) / 10 : null;
}

function mediaMatches(query) {
  try {
    return typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : null;
  } catch (_) {
    return null;
  }
}

function firstMatch(feature, values) {
  if (typeof window.matchMedia !== 'function') return null;
  for (const value of values) {
    if (mediaMatches(`(${feature}: ${value})`)) return value;
  }
  return null;
}

// The notch margins, measured the way SafeArea (ui/safe-area.ts) does: a
// throwaway probe whose padding is the env() values. SafeArea itself is not
// imported: the landing page's first bundle segment owns it, and this module
// loads in the second (tests/unit/client-bundle/pageBundleIsolation.test.js).
function measureInsets() {
  const zero = { top: 0, right: 0, bottom: 0, left: 0 };
  if (!document.documentElement || typeof window.getComputedStyle !== 'function') return zero;
  const probe = document.createElement('div');
  probe.setAttribute('data-replay-context-probe', '');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = [
    'position:fixed',
    'visibility:hidden',
    'pointer-events:none',
    'padding-top:env(safe-area-inset-top, 0px)',
    'padding-right:env(safe-area-inset-right, 0px)',
    'padding-bottom:env(safe-area-inset-bottom, 0px)',
    'padding-left:env(safe-area-inset-left, 0px)',
  ].join(';');
  document.documentElement.appendChild(probe);
  try {
    const style = window.getComputedStyle(probe);
    const px = (value) => {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    return { top: px(style.paddingTop), right: px(style.paddingRight), bottom: px(style.paddingBottom), left: px(style.paddingLeft) };
  } finally {
    probe.remove();
  }
}

function measure() {
  const insets = measureInsets();
  const vv = window.visualViewport || null;
  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    safe_area_top: round1(insets.top),
    safe_area_right: round1(insets.right),
    safe_area_bottom: round1(insets.bottom),
    safe_area_left: round1(insets.left),
    layout_width: round1(width),
    layout_height: round1(height),
    visual_width: vv ? round1(vv.width) : null,
    visual_height: vv ? round1(vv.height) : null,
    visual_offset_top: vv ? round1(vv.offsetTop) : null,
    visual_offset_left: vv ? round1(vv.offsetLeft) : null,
    visual_scale: vv ? round1(vv.scale) : null,
    device_pixel_ratio: round1(window.devicePixelRatio),
    orientation: width >= height ? 'landscape' : 'portrait',
    pointer: firstMatch('pointer', ['coarse', 'fine', 'none']),
    any_pointer_coarse: mediaMatches('(any-pointer: coarse)'),
    hover: firstMatch('hover', ['hover', 'none']),
    max_touch_points: typeof navigator.maxTouchPoints === 'number' ? navigator.maxTouchPoints : null,
    display_mode: firstMatch('display-mode', ['fullscreen', 'standalone', 'minimal-ui', 'browser']),
    // Safari before 16.4 has only the prefixed property.
    fullscreen: Boolean(document.fullscreenElement || /** @type {any} */ (document).webkitFullscreenElement),
    visible: document.visibilityState !== 'hidden',
    online: navigator.onLine !== false,
    reduced_motion: mediaMatches('(prefers-reduced-motion: reduce)'),
    color_scheme: firstMatch('prefers-color-scheme', ['dark', 'light']),
  };
}

/**
 * Starts the device_context events. `capture(event, props)` sends one;
 * `isActive()` says whether analytics may run right now. Returns { stop }.
 */
function startReplayContext({ capture, isActive }) {
  let lastSent = null;
  let sentCount = 0;
  let timer = null;
  let stopped = false;
  const vv = window.visualViewport || null;

  function send(reason) {
    timer = null;
    if (stopped || sentCount >= MAX_EVENTS_PER_PAGE) return;
    try {
      if (!isActive()) return;
      const snapshot = measure();
      const key = JSON.stringify(snapshot);
      if (key === lastSent) return;
      lastSent = key;
      sentCount += 1;
      capture('device_context', Object.assign({ reason: sentCount === 1 ? 'start' : reason }, snapshot));
    } catch (_) {
      // Analytics must never break the page.
    }
  }

  function schedule() {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => send('change'), SETTLE_MS);
  }

  const windowEvents = ['resize', 'orientationchange', 'fullscreenchange', 'webkitfullscreenchange', 'online', 'offline'];
  windowEvents.forEach((type) => window.addEventListener(type, schedule, { passive: true }));
  document.addEventListener('visibilitychange', schedule);
  if (vv && typeof vv.addEventListener === 'function') vv.addEventListener('resize', schedule);
  timer = setTimeout(() => send('start'), FIRST_SEND_MS);

  return {
    stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
      timer = null;
      windowEvents.forEach((type) => window.removeEventListener(type, schedule));
      document.removeEventListener('visibilitychange', schedule);
      if (vv && typeof vv.removeEventListener === 'function') vv.removeEventListener('resize', schedule);
    },
  };
}

export { REPLAY_VISIBLE_FIELDS, replayInputText, startReplayContext };
